import { authApi } from '@/features/auth/api/authApi'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { userApi } from '../api/userApi'
import { CreateUserModel, UpdateUserDepartmentsModel } from '../types/user'

export function useGetUsers(role?: string, enabled = true) {
  return useQuery({
    queryKey: ['users', role],
    queryFn: () => userApi.getUsers(role),
    retry: false,
    refetchOnWindowFocus: false,
    enabled,
  })
}

export function useGetUserById(id: string | null | undefined) {
  return useQuery({
    queryKey: ['user', id],
    queryFn: () => userApi.getUserById(id!),
    enabled: !!id,
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: userApi.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CreateUserModel> }) => userApi.updateUser(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useUpdateUserDepartments() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateUserDepartmentsModel }) =>
      userApi.updateUserDepartments(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['user', variables.id] })
    },
  })
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: (id: string) => authApi.resetPassword(id),
  })
}

export function useDeleteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => userApi.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}
